import { apiClient } from "../client";

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface PaginatedResponse<T> {
  message: string;
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface AxiosErrorResponse {
  response?: {
    data: ApiError;
  };
  message: string;
}

export interface Location {
  id: number;
  name: string;
  systemCode: string;
  siteId: number;
  organizationId: number;
  isActive: boolean;
  createdBy: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLocationDto {
  name: string;
  siteIds: number[];
}

export interface UpdateLocationDto {
  name?: string;
  siteIds?: number[];
}

export interface LocationResponse {
  message: string;
  locations: Location[];
}

export interface LocationUpdateResponse {
  message: string;
  location: Location;
}

export interface ComplianceStatus {
  id: number;
  name: string;
  description?: string;
  systemCode: string;
  siteId: number;
  organizationId: number;
  isActive: boolean;
  createdBy: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateComplianceStatusDto {
  name: string;
  description?: string;
  siteIds: number[];
}

export interface UpdateComplianceStatusDto {
  name?: string;
  description?: string;
  siteIds?: number[];
}

export interface ComplianceStatusResponse {
  message: string;
  complianceStatuses: ComplianceStatus[];
}

export interface ComplianceStatusUpdateResponse {
  message: string;
  complianceStatus: ComplianceStatus;
}

export interface HealthStatus {
  id: number;
  name: string;
  color?: string;
  description?: string;
  systemCode: string;
  siteId: number;
  organizationId: number;
  isActive: boolean;
  isDefault: boolean;
  createdBy: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHealthStatusDto {
  name: string;
  color?: string;
  description?: string;
  siteIds: number[];
}

export interface UpdateHealthStatusDto {
  name?: string;
  color?: string;
  description?: string;
  siteIds?: number[];
}

export interface HealthStatusResponse {
  message: string;
  healthStatuses: HealthStatus[];
}

export interface HealthStatusUpdateResponse {
  message: string;
  healthStatus: HealthStatus;
}

export interface AssetState {
  id: number;
  systemCode: string;
  name: string;
  description: string | null;
  siteId: number;
  organizationId: number;
  isDefault: boolean;
  isHidden: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAssetStateDto {
  name: string;
  description?: string;
  siteIds: number[];
}

export interface UpdateAssetStateDto {
  name?: string;
  description?: string;
  siteIds?: number[];
}

export interface AssetStateResponse {
  message: string;
  assetStates: AssetState[];
}

export interface AssetStateUpdateResponse {
  message: string;
  assetState: AssetState;
}

export interface Criticality {
  id: number;
  name: string;
  level?: number;
  description?: string;
  systemCode: string;
  siteId: number;
  organizationId: number;
  isActive: boolean;
  isDefault: boolean;
  createdBy: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCriticalityDto {
  name: string;
  level?: number;
  description?: string;
  siteIds: number[];
}

export interface UpdateCriticalityDto {
  name?: string;
  level?: number;
  description?: string;
  siteIds?: number[];
}

export interface CriticalityResponse {
  message: string;
  criticalities: Criticality[];
}

export interface CriticalityUpdateResponse {
  message: string;
  criticality: Criticality;
}

export interface Category {
  id: number;
  name: string;
  description?: string;
  systemCode: string;
  siteId: number;
  organizationId: number;
  isActive: boolean;
  createdBy: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryDto {
  name: string;
  description?: string;
  siteIds: number[];
}

export interface UpdateCategoryDto {
  name?: string;
  description?: string;
  siteIds?: number[];
}

export interface CategoryResponse {
  message: string;
  categories: Category[];
}

export interface CategoryUpdateResponse {
  message: string;
  category: Category;
}

export interface Department {
  id: number;
  name: string;
  description?: string;
  systemCode: string;
  siteId: number;
  organizationId: number;
  isActive: boolean;
  createdBy: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDepartmentDto {
  name: string;
  description?: string;
  siteIds: number[];
}

export interface UpdateDepartmentDto {
  name?: string;
  description?: string;
  siteIds?: number[];
}

export interface DepartmentResponse {
  message: string;
  departments: Department[];
}

export interface DepartmentUpdateResponse {
  message: string;
  department: Department;
}

export interface AllAttributesResponse {
  message: string;
  data: {
    locations: Location[];
    complianceStatuses: ComplianceStatus[];
    healthStatuses: HealthStatus[];
    assetStates: AssetState[];
    criticalities: Criticality[];
    categories: Category[];
    departments: Department[];
  };
}

export class GlobalAttributesService {
  private static getBaseUrl(organizationId: number): string {
    return `/organizations/${organizationId}/attributes`;
  }

  static async getAllAttributes(
    organizationId: number,
    siteId: number,
    params?: { search?: string },
  ): Promise<AllAttributesResponse> {
    return await apiClient.get<AllAttributesResponse>(
      `${this.getBaseUrl(organizationId)}/all/site/${siteId}`,
      { params },
    );
  }

  static async createLocation(
    organizationId: number,
    data: CreateLocationDto,
  ): Promise<LocationResponse> {
    return await apiClient.post<LocationResponse>(
      `${this.getBaseUrl(organizationId)}/locations`,
      data,
    );
  }

  static async getLocations(
    organizationId: number,
    siteId: number,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Location>> {
    return await apiClient.get<PaginatedResponse<Location>>(
      `${this.getBaseUrl(organizationId)}/locations/site/${siteId}`,
      { params },
    );
  }

  static async updateLocation(
    organizationId: number,
    siteId: number,
    locationId: number,
    data: UpdateLocationDto,
  ): Promise<LocationUpdateResponse> {
    return await apiClient.put<LocationUpdateResponse>(
      `${this.getBaseUrl(
        organizationId,
      )}/locations/${locationId}/site/${siteId}`,
      data,
    );
  }

  static async deleteLocation(
    organizationId: number,
    siteId: number,
    locationId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete<{ message: string }>(
      `${this.getBaseUrl(
        organizationId,
      )}/locations/${locationId}/site/${siteId}`,
    );
  }

  static async createComplianceStatus(
    organizationId: number,
    data: CreateComplianceStatusDto,
  ): Promise<ComplianceStatusResponse> {
    return await apiClient.post<ComplianceStatusResponse>(
      `${this.getBaseUrl(organizationId)}/compliance-statuses`,
      data,
    );
  }

  static async getComplianceStatuses(
    organizationId: number,
    siteId: number,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<ComplianceStatus>> {
    return await apiClient.get<PaginatedResponse<ComplianceStatus>>(
      `${this.getBaseUrl(organizationId)}/compliance-statuses/site/${siteId}`,
      { params },
    );
  }

  static async updateComplianceStatus(
    organizationId: number,
    siteId: number,
    statusId: number,
    data: UpdateComplianceStatusDto,
  ): Promise<ComplianceStatusUpdateResponse> {
    return await apiClient.put<ComplianceStatusUpdateResponse>(
      `${this.getBaseUrl(
        organizationId,
      )}/compliance-statuses/${statusId}/site/${siteId}`,
      data,
    );
  }

  static async deleteComplianceStatus(
    organizationId: number,
    siteId: number,
    statusId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete<{ message: string }>(
      `${this.getBaseUrl(
        organizationId,
      )}/compliance-statuses/${statusId}/site/${siteId}`,
    );
  }

  static async createHealthStatus(
    organizationId: number,
    data: CreateHealthStatusDto,
  ): Promise<HealthStatusResponse> {
    return await apiClient.post<HealthStatusResponse>(
      `${this.getBaseUrl(organizationId)}/health-statuses`,
      data,
    );
  }

  static async getHealthStatuses(
    organizationId: number,
    siteId: number,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<HealthStatus>> {
    return await apiClient.get<PaginatedResponse<HealthStatus>>(
      `${this.getBaseUrl(organizationId)}/health-statuses/site/${siteId}`,
      { params },
    );
  }

  static async updateHealthStatus(
    organizationId: number,
    siteId: number,
    healthId: number,
    data: UpdateHealthStatusDto,
  ): Promise<HealthStatusUpdateResponse> {
    return await apiClient.put<HealthStatusUpdateResponse>(
      `${this.getBaseUrl(
        organizationId,
      )}/health-statuses/${healthId}/site/${siteId}`,
      data,
    );
  }

  static async deleteHealthStatus(
    organizationId: number,
    siteId: number,
    healthId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete<{ message: string }>(
      `${this.getBaseUrl(
        organizationId,
      )}/health-statuses/${healthId}/site/${siteId}`,
    );
  }

  static async createAssetState(
    organizationId: number,
    data: CreateAssetStateDto,
  ): Promise<AssetStateResponse> {
    return await apiClient.post<AssetStateResponse>(
      `${this.getBaseUrl(organizationId)}/asset-states`,
      data,
    );
  }

  static async getAssetStates(
    organizationId: number,
    siteId: number,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<AssetState>> {
    return await apiClient.get<PaginatedResponse<AssetState>>(
      `${this.getBaseUrl(organizationId)}/asset-states/site/${siteId}`,
      { params },
    );
  }

  static async updateAssetState(
    organizationId: number,
    siteId: number,
    stateId: number,
    data: UpdateAssetStateDto,
  ): Promise<AssetStateUpdateResponse> {
    return await apiClient.put<AssetStateUpdateResponse>(
      `${this.getBaseUrl(
        organizationId,
      )}/asset-states/${stateId}/site/${siteId}`,
      data,
    );
  }

  static async deleteAssetState(
    organizationId: number,
    siteId: number,
    stateId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete<{ message: string }>(
      `${this.getBaseUrl(
        organizationId,
      )}/asset-states/${stateId}/site/${siteId}`,
    );
  }

  static async createCriticality(
    organizationId: number,
    data: CreateCriticalityDto,
  ): Promise<CriticalityResponse> {
    return await apiClient.post<CriticalityResponse>(
      `${this.getBaseUrl(organizationId)}/criticalities`,
      data,
    );
  }

  static async getCriticalities(
    organizationId: number,
    siteId: number,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Criticality>> {
    return await apiClient.get<PaginatedResponse<Criticality>>(
      `${this.getBaseUrl(organizationId)}/criticalities/site/${siteId}`,
      { params },
    );
  }

  static async updateCriticality(
    organizationId: number,
    siteId: number,
    criticalityId: number,
    data: UpdateCriticalityDto,
  ): Promise<CriticalityUpdateResponse> {
    return await apiClient.put<CriticalityUpdateResponse>(
      `${this.getBaseUrl(
        organizationId,
      )}/criticalities/${criticalityId}/site/${siteId}`,
      data,
    );
  }

  static async deleteCriticality(
    organizationId: number,
    siteId: number,
    criticalityId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete<{ message: string }>(
      `${this.getBaseUrl(
        organizationId,
      )}/criticalities/${criticalityId}/site/${siteId}`,
    );
  }

  static async createCategory(
    organizationId: number,
    data: CreateCategoryDto,
  ): Promise<CategoryResponse> {
    return await apiClient.post<CategoryResponse>(
      `${this.getBaseUrl(organizationId)}/categories`,
      data,
    );
  }

  static async getCategories(
    organizationId: number,
    siteId: number,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Category>> {
    return await apiClient.get<PaginatedResponse<Category>>(
      `${this.getBaseUrl(organizationId)}/categories/site/${siteId}`,
      { params },
    );
  }

  static async updateCategory(
    organizationId: number,
    siteId: number,
    categoryId: number,
    data: UpdateCategoryDto,
  ): Promise<CategoryUpdateResponse> {
    return await apiClient.put<CategoryUpdateResponse>(
      `${this.getBaseUrl(
        organizationId,
      )}/categories/${categoryId}/site/${siteId}`,
      data,
    );
  }

  static async deleteCategory(
    organizationId: number,
    siteId: number,
    categoryId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete<{ message: string }>(
      `${this.getBaseUrl(
        organizationId,
      )}/categories/${categoryId}/site/${siteId}`,
    );
  }

  static async createDepartment(
    organizationId: number,
    data: CreateDepartmentDto,
  ): Promise<DepartmentResponse> {
    return await apiClient.post<DepartmentResponse>(
      `${this.getBaseUrl(organizationId)}/departments`,
      data,
    );
  }

  static async getDepartments(
    organizationId: number,
    siteId: number,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Department>> {
    return await apiClient.get<PaginatedResponse<Department>>(
      `${this.getBaseUrl(organizationId)}/departments/site/${siteId}`,
      { params },
    );
  }

  static async updateDepartment(
    organizationId: number,
    siteId: number,
    departmentId: number,
    data: UpdateDepartmentDto,
  ): Promise<DepartmentUpdateResponse> {
    return await apiClient.put<DepartmentUpdateResponse>(
      `${this.getBaseUrl(
        organizationId,
      )}/departments/${departmentId}/site/${siteId}`,
      data,
    );
  }

  static async deleteDepartment(
    organizationId: number,
    siteId: number,
    departmentId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete<{ message: string }>(
      `${this.getBaseUrl(
        organizationId,
      )}/departments/${departmentId}/site/${siteId}`,
    );
  }
  static async toggleAssetStateVisibility(
    organizationId: number,
    siteId: number,
    stateId: number,
    isHidden: boolean,
  ): Promise<AssetState> {
    return await apiClient.put(
      `/organizations/${organizationId}/attributes/asset-states/${stateId}/site/${siteId}/visibility`,
      { isHidden },
    );
  }
}
